// ICS 204 Assignment List
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import ICSWidget from '../ICSWidget';
import { getIncidentCardTitle } from '../../USCGPage';
import { useFemaOpsBriefingFlag } from 'utils/featureFlags';
import AssignmentListFilters from './AssignmentListFilters';
import AssignmentListTable from './AssignmentListTable';
import {
  useWorkAssignments,
  ResourceNeeds,
} from 'components/IAP/hooks/workAssignmentHooks';
import {
  PlannedResource,
  useWorkAssignmentListAttachments,
  type WalaSignature,
} from 'components/IAP/hooks/assignmentListHooks';
import { useIncidentPermissions } from 'components/IAP/hooks/incidentHooks';
import dayjs from 'dayjs';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CreateAssignmentCardDialog, {
  PickerMode,
} from './CreateAssignmentCardDialog';
import {
  mapProposedStatus,
  type ProposedStatusOption,
} from './assignmentStatus';
import { formatIcs204StatusCell } from './ics204SignatureSlots';

export type WorkGroupResourceNeed = ResourceNeeds & {
  workAssignmentId: string;
  workAssignmentName: string;
};

export type { ProposedStatusOption, Status } from './assignmentStatus';
export { mapProposedStatus } from './assignmentStatus';

export type SimplifiedWorkAssignment = {
  id: string;
  type: string;
  name: string;
  description: string;
  workGroupName?: string;
  workGroupId?: string;
  overheadPosition?: string;
  specialInstructions?: string;
  reportingLocation?: string;
  requestedArrivalTime?: string;
  timestamp?: string;
};

type AssignmentListItem = {
  id?: string;
  walaId: string | null;
  workGroupId: string;
  workGroup: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  proposedStatus: ProposedStatusOption;
  actualStatus: string | null;
  archived: boolean;
  sectionChief?: string;
  sectionChiefContact?: string;
  branchDirector?: string;
  branchDirectorContact?: string;
  divisionGroupSupervisor?: string;
  divisionGroupSupervisorContact?: string;
  workAssignments: SimplifiedWorkAssignment[];
  workGroupResourceNeeds: WorkGroupResourceNeed[];
  plannedResources: PlannedResource[] | null;
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
  signatures?: WalaSignature[];
  updatedAt?: string;
  updatedByUserName?: string | null;
};

type AssignmentListColumnKey = Exclude<
  keyof AssignmentListItem,
  | 'id'
  | 'archived'
  | 'workGroupId'
  | 'walaId'
  | 'actualStatus'
  | 'sectionChief'
  | 'sectionChiefContact'
  | 'branchDirector'
  | 'branchDirectorContact'
  | 'divisionGroupSupervisor'
  | 'divisionGroupSupervisorContact'
  | 'workAssignments'
  | 'signatures'
  | 'updatedAt'
  | 'updatedByUserName'
>;

type AssignmentListProps = {
  periodNumber: number;
  /** When true, force cards read-only (historical OP view). */
  readOnly?: boolean;
};

/**
 * Stands in for rows whose value for a column is missing or blank, so a user
 * can filter _for_ the gaps. Only offered when a column actually has one.
 */
const BLANK_OPTION = '(Blank)';

const isBlankValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim().length === 0);

/** Flatten scalar + nested list fields for case-insensitive keyword search. */
const collectSearchableText = (value: unknown, parts: string[]): void => {
  if (value === null || value === undefined || typeof value === 'boolean') {
    return;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) parts.push(trimmed);
    return;
  }
  if (typeof value === 'number') {
    parts.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectSearchableText(entry, parts);
    }
    return;
  }
  if (typeof value === 'object') {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      collectSearchableText(nested, parts);
    }
  }
};

const matchesKeywordSearch = (item: AssignmentListItem, query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  const parts: string[] = [];
  collectSearchableText(item, parts);
  const statusCell = formatIcs204StatusCell({
    proposedStatusLabel: item.proposedStatus,
    signatures: item.signatures,
  });
  parts.push(statusCell.primary, statusCell.signaturesLine);
  return parts.join(' ').toLowerCase().includes(normalized);
};

const AssignmentList: React.FC<AssignmentListProps> = ({
  periodNumber,
  readOnly: forceReadOnly = false,
}) => {
  const femaOps = useFemaOpsBriefingFlag();
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-assignment-list`;

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;
  const readOnly = forceReadOnly || !canEditCards;

  const [pickerMode, setPickerMode] = useState<PickerMode | null>(null);

  const { data: workAssignments } = useWorkAssignments();
  const { data: workAssignmentListAttachments } =
    useWorkAssignmentListAttachments();

  // Source the list entirely from WALAs for the current period.
  // 215 work assignments are used only for enrichment (matching work group IDs).
  const assignmentListFromWalas: AssignmentListItem[] = useMemo(() => {
    const walas = workAssignmentListAttachments?.filter(
      (a) => a.operationalPeriodNumber === periodNumber
    );
    if (!walas || walas.length === 0) return [];

    // Build a lookup: workGroupId → workAssignments in this period
    const waByGroup = new Map<string, SimplifiedWorkAssignment[]>();
    for (const wa of workAssignments ?? []) {
      if (wa.operational_period_number !== periodNumber) continue;
      const gid = wa.work_group_id;
      if (!gid) continue;
      const timestamp = wa.timestamp ? dayjs(wa.timestamp).toISOString() : '';
      const item: SimplifiedWorkAssignment = {
        id: wa.id,
        type: '',
        name: wa.work_assignment ?? '',
        description: '',
        workGroupName: wa.work_group_name ?? '',
        workGroupId: gid,
        overheadPosition: wa.overhead_positions ?? '',
        specialInstructions: wa.special_instructions ?? '',
        reportingLocation: wa.reporting_location ?? '',
        requestedArrivalTime: wa.requested_arrival_time
          ? dayjs(wa.requested_arrival_time).toISOString()
          : '',
        timestamp,
      };
      const existing = waByGroup.get(gid);
      if (existing) {
        existing.push(item);
      } else {
        waByGroup.set(gid, [item]);
      }
    }

    const resourceNeedsByGroup = new Map<string, WorkGroupResourceNeed[]>();
    for (const wa of workAssignments ?? []) {
      if (wa.operational_period_number !== periodNumber) continue;
      const gid = wa.work_group_id;
      if (!gid) continue;
      const needs =
        wa.resource_list?.map((r) => ({
          ...r,
          workAssignmentId: wa.id,
          workAssignmentName: wa.work_assignment ?? '',
        })) ?? [];
      const existing = resourceNeedsByGroup.get(gid) ?? [];
      resourceNeedsByGroup.set(gid, [...existing, ...needs]);
    }

    return walas.map((attachment) => ({
      id: attachment.id,
      walaId: attachment.walaId,
      workGroupId: attachment.workGroupId,
      workGroup: attachment.workGroupName,
      branch: attachment.branch,
      division: attachment.division,
      walaGroup: attachment.walaGroup,
      stagingArea: attachment.stagingArea,
      proposedStatus: mapProposedStatus(
        attachment.proposedStatus,
        attachment.status
      ),
      actualStatus: attachment.status,
      archived: attachment.archived ?? false,
      sectionChief: attachment.sectionChief,
      sectionChiefContact: attachment.sectionChiefContact,
      branchDirector: attachment.branchDirector,
      branchDirectorContact: attachment.branchDirectorContact,
      divisionGroupSupervisor: attachment.divisionGroupSupervisor,
      divisionGroupSupervisorContact: attachment.divisionGroupSupervisorContact,
      workAssignments: waByGroup.get(attachment.workGroupId) ?? [],
      workGroupResourceNeeds:
        resourceNeedsByGroup.get(attachment.workGroupId) ?? [],
      plannedResources: attachment.plannedResources,
      medicalCommunication: attachment.medicalCommunication,
      evacuationCommunication: attachment.evacuationCommunication,
      otherCommunication: attachment.otherCommunication,
      specialInstructions: attachment.specialInstructions,
      signatures: attachment.signatures ?? [],
      updatedAt: attachment.updatedAt,
      updatedByUserName: attachment.updatedByUserName ?? null,
    }));
  }, [workAssignmentListAttachments, workAssignments, periodNumber]);

  const filterColumnDefs = useMemo<
    Array<{ key: AssignmentListColumnKey; label: string }>
  >(
    () => [
      { key: 'workGroup', label: 'Assignee' },
      { key: 'branch', label: 'Branch' },
      { key: 'division', label: 'Division' },
      { key: 'walaGroup', label: 'Group' },
      { key: 'stagingArea', label: 'Staging Area' },
      { key: 'proposedStatus', label: 'Status' },
    ],
    []
  );

  const filterColumns = useMemo(() => {
    const visible = assignmentListFromWalas.filter((item) => !item.archived);

    return filterColumnDefs.map(({ key, label }) => {
      const values = visible.map((item) => item[key]);
      const options = Array.from(
        new Set(
          values.filter(
            (v): v is string => typeof v === 'string' && v.trim().length > 0
          )
        )
      ).sort((a, b) => a.localeCompare(b));

      return {
        key,
        label,
        options: values.some(isBlankValue)
          ? [...options, BLANK_OPTION]
          : options,
      };
    });
  }, [filterColumnDefs, assignmentListFromWalas]);

  const defaultFilters = useMemo(
    () =>
      Object.fromEntries(
        filterColumnDefs.map((column) => [column.key, [] as string[]])
      ),
    [filterColumnDefs]
  );

  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, string[]>>(defaultFilters);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredList = useMemo(() => {
    return assignmentListFromWalas.filter((item) => {
      // Values within a column are OR'd; columns are AND'd.
      const matchesColumns = filterColumnDefs.every(({ key }) => {
        const picked = selectedFilters[key];
        if (!picked?.length) {
          return true;
        }
        const value = item[key];
        return isBlankValue(value)
          ? picked.includes(BLANK_OPTION)
          : picked.includes(String(value));
      });
      return matchesColumns && matchesKeywordSearch(item, searchQuery);
    });
  }, [assignmentListFromWalas, filterColumnDefs, selectedFilters, searchQuery]);

  const toggleFilter = (columnKey: string, value: string) => {
    setSelectedFilters((prev) => {
      const picked = prev[columnKey] ?? [];
      return {
        ...prev,
        [columnKey]: picked.includes(value)
          ? picked.filter((v) => v !== value)
          : [...picked, value],
      };
    });
  };

  const clearFilters = () => {
    setSelectedFilters(defaultFilters);
  };

  const hasActiveFilters = Object.values(selectedFilters).some(
    (picked) => picked.length > 0
  );
  const hasActiveSearch = searchQuery.trim().length > 0;
  const hasActiveConstraints = hasActiveFilters || hasActiveSearch;

  const alreadyAddedWorkGroupIds = useMemo(
    () => assignmentListFromWalas.map((item) => item.workGroupId),
    [assignmentListFromWalas]
  );

  const visibleRows = useMemo(
    () => filteredList.filter((item) => !item.archived),
    [filteredList]
  );

  return (
    <div data-tour="ics204-card">
      {pickerMode !== null && (
        <CreateAssignmentCardDialog
          show={pickerMode !== null}
          onClose={() => setPickerMode(null)}
          pickerMode={pickerMode}
          alreadyAddedWorkGroupIds={alreadyAddedWorkGroupIds}
          periodNumber={periodNumber}
          workAssignments={workAssignments}
        />
      )}

      <CollapsibleCard
        sectionKey={sectionKey}
        collapsible={false}
        dataTestId="ics204-widget"
        className="planning-widget planning-widget--borderless"
        contentClassName="content mt-2"
        headerLeft={
          <ICSWidget.HeaderLeft
            iconName="assignment"
            iconColor="primary"
            title={
              femaOps
                ? 'Mission Assignment List'
                : getIncidentCardTitle('assignmentList')
            }
            subtitle=""
          />
        }
        headerRight={
          <div className="d-flex align-items-center gap-2">
            {!readOnly && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <StylishNewButton
                      className="button button--secondary tw:inline-flex tw:items-center"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="ics204-create-dropdown"
                    >
                      + Create
                      <ChevronDown className="tw:ml-1 tw:h-4 tw:w-4" />
                    </StylishNewButton>
                  }
                />
                <DropdownMenuContent
                  align="end"
                  sideOffset={4}
                  className="dark"
                >
                  <DropdownMenuItem
                    data-testid="ics204-create-from-215"
                    onClick={() => setPickerMode('from215')}
                  >
                    + Create From ICS-215
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="ics204-create-from-scratch"
                    onClick={() => setPickerMode('fromScratch')}
                  >
                    + Create ICS-204 From Scratch
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        }
      >
        <AssignmentListFilters
          columns={filterColumns}
          selected={selectedFilters}
          onToggle={toggleFilter}
          onClearAll={clearFilters}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        {visibleRows.length > 0 ? (
          <AssignmentListTable
            data={visibleRows}
            periodNumber={periodNumber}
            readOnly={readOnly}
            alreadyAddedWorkGroupIds={alreadyAddedWorkGroupIds}
          />
        ) : hasActiveConstraints ? (
          <div className="section-main" data-testid="ics204-no-matches">
            No assignment cards match the current search or filters <br />
            <br />
            Clear the search or remove a condition above to widen the results.
          </div>
        ) : (
          <div className="section-main">
            No assignment cards defined <br />
            <br />
            {!readOnly
              ? 'Use "+ Create" to create an assignment card.'
              : 'No assignment cards have been created for this period yet.'}
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
};

export default AssignmentList;
